import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";

import { AdminLayout } from "@/components/AdminLayout";
import { Layout } from "@/components/Layout";
import { MustChangePasswordGuard } from "@/components/MustChangePasswordGuard";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireModule } from "@/components/RequireModule";
import { RequirePermission } from "@/components/RequirePermission";
import { useSystemSettings } from "@/lib/system";
import AcceptInvite from "@/pages/AcceptInvite";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import FlowDetail from "@/pages/FlowDetail";
import FlowsList from "@/pages/FlowsList";
import Login from "@/pages/Login";
import ManufacturingOrderDetail from "@/pages/ManufacturingOrderDetail";
import ManufacturingOrderForm from "@/pages/ManufacturingOrderForm";
import ManufacturingOrdersList from "@/pages/ManufacturingOrdersList";
import OrderDetail from "@/pages/OrderDetail";
import OrdersList from "@/pages/OrdersList";
import Passport from "@/pages/Passport";
import Print from "@/pages/Print";
import Settings from "@/pages/Settings";
import AdminHome from "@/pages/admin/AdminHome";
import ApiTokensAdmin from "@/pages/admin/ApiTokensAdmin";
import AuditLog from "@/pages/admin/AuditLog";
import CycleEditor from "@/pages/admin/CycleEditor";
import MembraneDashboard from "@/pages/admin/MembraneDashboard";
import RigKiosk from "@/pages/RigKiosk";
import MembraneSessions from "@/pages/admin/MembraneSessions";
import MembraneTests from "@/pages/admin/MembraneTests";
import MetricsEditor from "@/pages/admin/MetricsEditor";
import IntegrationsErp from "@/pages/admin/IntegrationsErp";
import Inventory from "@/pages/admin/Inventory";
import PrintAgentDetail from "@/pages/admin/PrintAgentDetail";
import PrintAgentsList from "@/pages/admin/PrintAgentsList";
import PrintHistory from "@/pages/admin/PrintHistory";
import PrintTemplateDetail from "@/pages/admin/PrintTemplateDetail";
import PrintTemplatesList from "@/pages/admin/PrintTemplatesList";
import ReferenceLists from "@/pages/admin/ReferenceLists";
import CompanyProfile from "@/pages/admin/CompanyProfile";
import RoleDetail from "@/pages/admin/RoleDetail";
import RolesList from "@/pages/admin/RolesList";
import StationAssignments from "@/pages/admin/StationAssignments";
import StationDetail from "@/pages/admin/StationDetail";
import StationsList from "@/pages/admin/StationsList";
import TagsList from "@/pages/admin/TagsList";
import InvitationsList from "@/pages/admin/InvitationsList";
import SystemSettings from "@/pages/admin/SystemSettings";
import TestReportForm from "@/pages/admin/TestReportForm";
import TestReportHistory from "@/pages/admin/TestReportHistory";
import PackingListForm from "@/pages/admin/PackingListForm";
import PassportForm from "@/pages/admin/PassportForm";
import PassportHistory from "@/pages/admin/PassportHistory";
import DocumentCenter from "@/pages/admin/DocumentCenter";
import StageDetail from "@/pages/StageDetail";
import StagesList from "@/pages/StagesList";
import Traceability from "@/pages/Traceability";
import UserDetail from "@/pages/admin/UserDetail";
import UsersList from "@/pages/admin/UsersList";

export default function App() {
  const appTitle = useSystemSettings().data?.company.app_title;
  useEffect(() => {
    if (appTitle) document.title = appTitle;
  }, [appTitle]);
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      {/* Киоск: панель стенда без логина, авторизация PAT-токеном из URL. */}
      <Route path="/kiosk/rig" element={<RigKiosk />} />
      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePassword />
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <MustChangePasswordGuard>
              <Layout />
            </MustChangePasswordGuard>
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="stages" element={gate(STAGE_READ, <StagesList />)} />
        <Route path="stages/:id" element={gate(STAGE_READ, <StageDetail />)} />
        <Route path="orders" element={gate("order.read", <OrdersList />)} />
        <Route path="orders/:id" element={gate("order.read", <OrderDetail />)} />
        <Route path="manufacturing-orders" element={gate("manufacturing_order.read", <ManufacturingOrdersList />)} />
        <Route path="manufacturing-orders/new" element={gate("manufacturing_order.create", <ManufacturingOrderForm />)} />
        <Route path="manufacturing-orders/:id" element={gate("manufacturing_order.read", <ManufacturingOrderDetail />)} />
        <Route path="manufacturing-orders/:id/edit" element={gate(["manufacturing_order.create", "manufacturing_order.update"], <ManufacturingOrderForm />)} />
        <Route path="flows" element={gate("order.read", <FlowsList />)} />
        <Route path="flows/:id" element={gate("order.read", <FlowDetail />)} />
        <Route path="traceability" element={gate("order.read", <Traceability />)} />
        <Route path="flows/:id/passport" element={gate("order.read", <Passport />)} />
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminHome />} />
          <Route path="users" element={gate("user.manage", <UsersList />)} />
          <Route path="users/:id" element={gate("user.manage", <UserDetail />)} />
          <Route path="invitations" element={gate("user.manage", <InvitationsList />)} />
          <Route path="api-tokens" element={gate("user.manage", <ApiTokensAdmin />)} />
          <Route path="settings/system" element={gate("user.manage", <SystemSettings />)} />
          <Route path="settings/company" element={gate("user.manage", <CompanyProfile />)} />
          <Route path="roles" element={gate(["role.manage", "user.manage"], <RolesList />)} />
          <Route path="roles/:role" element={gate("role.manage", <RoleDetail />)} />
          <Route path="audit" element={gate("audit.read", <AuditLog />)} />
          <Route path="stations" element={gate("station.manage", <StationsList />)} />
          <Route path="stations/:id" element={gate("station.manage", <StationDetail />)} />
          <Route path="station-assignments" element={gate("station.assign", <StationAssignments />)} />
          <Route path="tags" element={gate("tag.manage", <TagsList />)} />
          <Route path="print-templates" element={gate("print.template.manage", <PrintTemplatesList />)} />
          <Route path="print-templates/:id" element={gate("print.template.manage", <PrintTemplateDetail />)} />
          <Route path="print-agents" element={gate("print.agent.manage", <PrintAgentsList />)} />
          <Route path="print-agents/:id" element={gate("print.agent.manage", <PrintAgentDetail />)} />
          <Route path="integrations/erp" element={gate("integration.manage", <IntegrationsErp />)} />
          <Route path="cycle" element={gate("cycle.manage", <CycleEditor />)} />
          <Route path="metrics" element={gate("cycle.manage", <MetricsEditor />)} />
          <Route path="reference" element={gate("cycle.manage", <ReferenceLists />)} />
          <Route path="inventory" element={gate(["inventory.read", "inventory.manage"], <Inventory />)} />
          <Route path="membrane-dashboard" element={gate("membrane_test.read", <MembraneDashboard />, "membrane-bench")} />
          <Route path="membrane-tests" element={gate("membrane_test.read", <MembraneTests />, "membrane-bench")} />
          <Route path="membrane-sessions" element={gate("membrane_test.read", <MembraneSessions />, "membrane-bench")} />
          <Route path="documents" element={gate("document.print", <DocumentCenter />)} />
          <Route path="print/history" element={gate("print.use", <PrintHistory />)} />
          <Route path="print/test-reports" element={gate(["document.test_report.manage", "document.print"], <TestReportForm />, "ro-test-report")} />
          <Route path="print/test-reports/history" element={gate(["document.test_report.manage", "document.print"], <TestReportHistory />, "ro-test-report")} />
          <Route path="print/packing-list" element={gate(["print.use", "document.print"], <PackingListForm />)} />
          <Route path="print/passports" element={gate("document.passport.manage", <PassportForm />)} />
          <Route path="print/passports/history" element={gate("document.passport.manage", <PassportHistory />)} />
          {/* W5: операторский хаб печати документов, обнаружимый из admin */}
          <Route path="print/issue" element={gate("print.use", <Print />)} />
        </Route>
        <Route path="print" element={gate("print.use", <Print />)} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

// Маршрут по праву: прямой URL без права — карточка 403.  Коды — те же,
// что у пунктов меню (AdminLayout) и на бэкенде.
const STAGE_READ = ["order.read", "stage.execute"];

function gate(codes: string | string[], element: ReactNode, module?: string) {
  const guarded = <RequirePermission codes={codes}>{element}</RequirePermission>;
  return module ? <RequireModule module={module}>{guarded}</RequireModule> : guarded;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="card p-10 text-center">
      <p className="font-serif text-3xl">404</p>
      <p className="text-text-muted mt-2">{t("common.notFound")}</p>
    </div>
  );
}
